import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { useStockByOfferIdQuery } from '@/domains/productDiscovery/ProductPage/common/modules/QuantitySelector/generated/stock.query.generated';
import {
  CHANGE_QUANTITY,
  SET_STOCK,
} from '@/domains/productDiscovery/ProductPage/common/modules/QuantitySelector/Quantity.ducks';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { useQuantity } from '@/domains/productDiscovery/ProductPage/hooks/useQuantity';
import type { Stock } from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';
import { useStock } from '@/productDiscovery/ProductPage/hooks/useStock';

interface UseQuantitySelector {
  quantity: number;
  isSellable: boolean | undefined;
  stock: Stock | undefined;
  onStepperChange: (value: string | number) => void;
  totalStock: number | null;
}

export const useQuantitySelector = (): UseQuantitySelector => {
  const dispatch = useDispatch();
  const { title, isSellable, offerId, stock } = useDetail();
  const quantity = useQuantity();
  const totalStock = useStock();

  const [{ data }] = useStockByOfferIdQuery({
    variables: { offerId },
    pause: quantity === stock.minimumValue,
  });

  useEffect(() => {
    if (data?.getAvailableStockByOfferId) {
      dispatch({ type: SET_STOCK, stock: data?.getAvailableStockByOfferId });
    }
  }, [data?.getAvailableStockByOfferId, dispatch]);

  const onStepperChange = useCallback(
    (value: string | number) => {
      Gtm.push({
        event: 'change_quantity',
        product_quantity: value,
      });

      Gtm.push({
        event: 'interaction_change product quantity',
        e_category: 'navigation',
        e_action: 'Change product quantity',
        event_trigger: title,
        amp_event_name: 'Change product quantity',
      });

      const number = typeof value === 'string' ? parseInt(value, 10) : value;
      dispatch({ type: CHANGE_QUANTITY, quantity: number });
    },
    [dispatch, title],
  );

  return {
    quantity,
    onStepperChange,
    stock,
    isSellable,
    totalStock,
  };
};
